import * as THREE from 'three';
import { coreGlow } from '../render/materials';
import { PROP } from './mech-kit';
import { LIK, LPOSE, LSTANCE } from './lancer-pose';
import { buildCockpit, buildSensor } from './mech-parts';
import { buildLancerCore } from './lancer-core';
import { buildLancerBlade, buildLancerShield } from './lancer-arm';
import { buildLancerBack, buildLancerLegs } from './lancer-legs';
import { equippedShape } from './mech-shape';
import { mergeBones } from './mech-merge';
import { newAnim } from './mech-anim';
/* ============================================================
   LANCER-2 組裝（骨架契約與 CHASSIS-0 完全相同）
   ============================================================

   三層骨架 `root > body > { lower, torso }` 與 `buildChassis0` 逐字相同 ——
   **這是刻意的**：動畫層（`mech-anim.ts`）、特效層（`render/mech-fx.ts`）、
   合併（`mergeBones`）、局外部件換裝（`mech-shape.ts`）四套系統都只認
   這個契約，不認機體。兩台機體的差異全部收在
   「哪些 builder」＋「rig.pose / rig.stance / rig.ik 三組常數」裡。

   舊版的 LANCER-2 是 `kind: 'legacy'` 的兩層骨架，享受不到上面任何一項
   （沒有待機呼吸、沒有開火後座、沒有噴焰、機庫換部件外觀不會變）。
   那條路徑連同 `mech-legacy.ts` 一起刪掉了，不要再加回來。

   實測全高 4.18（CHASSIS-0 是 4.90，矮 15%）、最大寬 4.41（它 4.53）——
   **寬高比 1.06 對 0.92**，這是兩台最好認的一個數字。腿只佔 47%、
   軀幹反而比它長：長軀幹＋短四肢就是健體體型的定義。
   完整的比例對照與四輪實拍踩到的坑見 `.claude/notes/lancer2-design.md`。 */
export function buildLancer2(loadout?) {
  var s = loadout || {
    core: equippedShape('core', 'core-std', 'lancer2'),
    cockpit: equippedShape('cockpit', 'cock-std', 'lancer2'),
    thruster: equippedShape('thruster', 'thr-twin', 'lancer2'),
    main: equippedShape('main', 'main-vulcan', 'lancer2'),
    sensor: equippedShape('sensor', 'sen-std', 'lancer2'),
    pylon: equippedShape('pylon', 'pyl-std', 'lancer2')
  };
  var P = { glow: coreGlow(s.core) };
  var root = new THREE.Group();
  var parts: any = {};

  var body = new THREE.Group(); (body.userData as any).bone = 1;
  body.position.y = LSTANCE.bodyY;
  root.add(body);

  var lower = buildLancerLegs(s.thruster, P);
  (lower.userData as any).bone = 1;
  lower.rotation.y = LPOSE.hipYaw;
  parts.thruster = lower; body.add(lower);

  var torso = buildLancerCore(s.core, P);
  (torso.userData as any).bone = 1;
  torso.position.y = 1.78 - LSTANCE.bodyY;      // 腰線在世界 1.78
  torso.rotation.set(LPOSE.torsoPitch, 0, LPOSE.torsoRoll);
  parts.core = torso; body.add(torso);

  var back = buildLancerBack(s.thruster, P);
  (back.userData as any).bone = 1;
  back.position.set(0, 0.70, -0.58);
  parts.back = back; torso.add(back);

  /* 頭縮到 0.88 倍再往下埋：肩承座在 local 1.10、肩甲上緣 1.48，
     頭頂只到 1.52 —— 「幾乎沒有脖子」是這台機體最好認的一個特徵。 */
  var head = buildCockpit(s.cockpit, P);
  (head.userData as any).bone = 1;
  head.position.set(0.10, 1.18, 0.02);
  head.scale.setScalar(0.88);
  head.rotation.set(LPOSE.headPitch, LPOSE.headYaw, -LPOSE.torsoRoll * 0.7);
  parts.cockpit = head; torso.add(head);

  var sen = buildSensor(s.sensor, P);
  sen.position.set(-0.26, 0.22, 0.03);
  parts.sensor = sen; head.add(sen);

  var pyl = buildLancerShield(s.pylon, P);
  (pyl.userData as any).bone = 1;
  pyl.position.set(1.02, 1.00, 0);
  pyl.rotation.set(0, LPOSE.pylonYaw, LPOSE.pylonRoll);
  parts.pylon = pyl; torso.add(pyl);

  var arm = buildLancerBlade(s.main, P);
  (arm.userData as any).bone = 1;
  arm.position.set(-1.02, 1.00, 0);
  parts.main = arm; torso.add(arm);

  var rig: any = {
    kind: 'v3', melee: 1, root: root, parts: parts, shapes: s,
    pose: LPOSE, stance: LSTANCE, ik: LIK,
    body: body, lower: lower, torso: torso, back: back, head: head, pylon: pyl,
    pelvis: (lower.userData as any).rig.pelvis,
    legs: (lower.userData as any).rig.legs,
    arm: (arm.userData as any).rig,
    muzzleY: (arm.userData as any).muzzle,
    nozzles: (back.userData as any).nozzles,
    vents: (back.userData as any).vents,
    glow: P.glow,
    muzzles: [], jets: []          // 舊介面的殘骸，見 `mechs.ts` 同名欄位的說明
  };
  (root.userData as any).rig = rig;

  mergeBones(root);                // **一定要在特效建立之前**（見 `mechs.ts`）
  rig.anim = newAnim();
  root.scale.setScalar(PROP.scale);
  return rig;
}
